import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Loader2, ChevronDown, ChevronUp, AlertCircle, Coins,
  ClipboardList, ListChecks, LayoutGrid, Stethoscope, Beaker, ShieldAlert, HeartHandshake,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const CATEGORY_TONE = {
  "Recommended": "border-teal-700 bg-teal-50 text-teal-900",
  "Consider selectively": "border-amber-500 bg-amber-50 text-amber-900",
  "Not routinely recommended": "border-slate-400 bg-slate-50 text-slate-700",
};

const URGENCY_TONE = {
  urgent: "text-red-700 font-bold",
  soon: "text-amber-700 font-semibold",
  routine: "text-slate-500",
};

const EMPTY_EXT = {
  age: "", bmi: "", infertility_years: "", infertility_type: "",
  obstetric_history: "", medications: "", allergies: "", lifestyle: "",
  amh: "", afc: "", tubal_status: "", uterine_findings: "", male_factor: "",
  ivf_cycle_history: "", endometrial_prep: "", luteal_support: "",
  rpl_losses_detail: "", poc_karyotype: "", parental_karyotype: "", aps_results: "",
  additional_notes: "",
};

/**
 * ClinicalReviewPanel — collects optional extended intake fields, kicks off a
 * background AI review (Claude Sonnet 5), polls until ready, and renders the
 * 8-section clinician-facing report.
 *
 * Props:
 *  - caseId: string
 *  - endpointBase: string   e.g. "/cases" (RIF) or "/rpl/cases" (RPL)
 *  - module: "rif" | "rpl"
 *  - initialReview?: object
 *  - initialStatus?: "processing" | "ready" | "error" | null
 *  - initialExtended?: object
 *  - onReviewReady?: (review) => void  // parent may refresh PDF button state etc.
 */
export function ClinicalReviewPanel({
  caseId, endpointBase, module,
  initialReview = null, initialStatus = null, initialExtended = null,
  onReviewReady,
}) {
  const { user } = useAuth();
  const [expandedIntake, setExpandedIntake] = useState(false);
  const [ext, setExt] = useState({ ...EMPTY_EXT, ...(initialExtended || {}) });
  const [review, setReview] = useState(initialReview);
  const [status, setStatus] = useState(initialStatus || (initialReview ? "ready" : null));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const pollingRef = useRef(null);

  const rplMode = module === "rpl";

  const refreshQuota = async () => {
    try {
      const { data } = await api.get("/ai-review/quota");
      setQuota(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { refreshQuota(); }, []);

  // Poll GET /cases/{id} for background job status changes and refresh quota
  // once the review lands.
  const startPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`${endpointBase}/${caseId}`);
        if (data.clinical_review_status === "ready" && data.clinical_review) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setReview(data.clinical_review);
          setStatus("ready");
          setError(null);
          refreshQuota();
          if (onReviewReady) onReviewReady(data.clinical_review);
        } else if (data.clinical_review_status === "error") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setStatus("error");
          setError(data.clinical_review_error || "AI review failed");
        }
      } catch (e) {
        // transient network — keep polling
      }
    }, 5000);
  };

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  useEffect(() => {
    if (initialStatus === "processing") startPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normalize extended payload — coerce numeric strings, drop empties.
  const buildExtendedPayload = () => {
    const out = {};
    const numericKeys = ["age", "afc"];
    const floatKeys = ["bmi", "infertility_years", "amh"];
    Object.entries(ext).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      if (numericKeys.includes(k)) {
        const n = parseInt(v, 10);
        if (!Number.isNaN(n)) out[k] = n;
      } else if (floatKeys.includes(k)) {
        const n = parseFloat(v);
        if (!Number.isNaN(n)) out[k] = n;
      } else {
        const s = String(v).trim();
        if (s) out[k] = s;
      }
    });
    return out;
  };

  // Open Razorpay checkout for the ₹49 extra-review payment; on success
  // re-post the review with signature so the backend can debit and generate.
  const runPaidReview = async (paymentIntent, extendedPayload, force) => {
    const ok = await loadRazorpay();
    if (!ok) throw new Error("Failed to load Razorpay checkout script.");
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: paymentIntent.key_id,
        amount: paymentIntent.amount * 100,
        currency: paymentIntent.currency || "INR",
        order_id: paymentIntent.order_id,
        name: "RIF Master",
        description: "Extra AI clinical review",
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        theme: { color: "#1e3a8a" },
        handler: async (resp) => {
          try {
            const { data } = await api.post(`${endpointBase}/${caseId}/clinical-review`, {
              extended: extendedPayload,
              force_regenerate: force,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            resolve(data);
          } catch (e) { reject(e); }
        },
        modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
      });
      rzp.on("payment.failed", (r) => reject(new Error(r?.error?.description || "Payment failed")));
      rzp.open();
    });
  };

  const generate = async (force = false) => {
    setBusy(true); setError(null);
    try {
      const payload = { extended: buildExtendedPayload(), force_regenerate: force };
      let { data } = await api.post(`${endpointBase}/${caseId}/clinical-review`, payload);

      // Cap hit → Razorpay ₹49 gate.
      if (data.status === "payment_required") {
        const goPay = window.confirm(
          `${data.message}\n\nOpen Razorpay to pay ₹${data.amount} for this extra review?`,
        );
        if (!goPay) { setBusy(false); return; }
        toast.info("Opening secure Razorpay checkout…");
        data = await runPaidReview(data, payload.extended, force);
      }

      if (data.status === "ready" && data.clinical_review) {
        setReview(data.clinical_review);
        setStatus("ready");
        refreshQuota();
        if (onReviewReady) onReviewReady(data.clinical_review);
      } else if (data.status === "processing") {
        setStatus("processing");
        setReview(null);
        toast.info("Deep clinical review started. This usually takes 60–120 seconds.");
        startPolling();
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setError(formatApiError(e));
      if (status !== "ready") setStatus("error");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-8 border-2 border-blue-900/30 rounded-md" data-testid="clinical-review-panel">
      <div className="bg-blue-900/5 px-5 py-4 border-b border-blue-900/20 flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-blue-900 flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-white" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-blue-900">Deep clinical review (AI-assisted)</div>
          <h3 className="mt-0.5 text-xl font-extrabold text-slate-900">
            Full 8-section {rplMode ? "RPL" : "RIF"} narrative report
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Runs your deterministic protocol above through Claude Sonnet 5, grounded on ASRM · ESHRE · ASRI · RCOG · ACOG guidance.
            Extra intake below is optional — the more you fill, the richer the report. Report is appended to the PDF export.
          </p>
        </div>
      </div>

      <div className="p-5 space-y-5">
        <IntakeSection
          expanded={expandedIntake}
          setExpanded={setExpandedIntake}
          ext={ext}
          setExt={setExt}
          rplMode={rplMode}
          disabled={status === "processing"}
        />

        {quota && <QuotaBadge quota={quota} />}

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={() => generate(status === "ready")}
            disabled={busy || status === "processing"}
            className="bg-blue-900 hover:bg-blue-800 h-11"
            data-testid="clinical-review-generate-btn">
            {status === "processing" ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.75} /> Generating…</>
            ) : status === "ready" ? (
              <><Sparkles className="h-4 w-4 mr-2" strokeWidth={1.75} /> Regenerate deep review{quota && quota.remaining_free === 0 ? ` (₹${quota.extra_price_inr})` : ""}</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" strokeWidth={1.75} /> Generate deep clinical review{quota && quota.remaining_free === 0 ? ` (₹${quota.extra_price_inr})` : ""}</>
            )}
          </Button>
          {status === "processing" && (
            <div className="text-xs text-slate-500" data-testid="clinical-review-processing-note">
              You can keep working — this runs in the background and will appear here in ~60–120s.
            </div>
          )}
        </div>

        {status === "error" && (
          <div className="border-l-4 border-red-600 bg-red-50 p-3 rounded-sm flex items-start gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-red-700 mt-0.5 shrink-0" strokeWidth={1.75} />
            <div>
              <div className="font-semibold text-red-800">Review failed</div>
              <div className="text-red-700 text-xs mt-1">{error}</div>
            </div>
          </div>
        )}

        {review && <ReviewReport review={review} />}
      </div>
    </div>
  );
}

function QuotaBadge({ quota }) {
  const used = quota.used || 0;
  const cap = quota.cap || 5;
  const remaining = quota.remaining_free;
  const paidExtras = quota.paid_extras || 0;
  const exhausted = remaining === 0;
  const tone = exhausted
    ? "border-amber-500 bg-amber-50 text-amber-900"
    : remaining <= 1
      ? "border-amber-400 bg-amber-50/60 text-slate-800"
      : "border-slate-300 bg-slate-50 text-slate-700";
  return (
    <div
      className={`flex items-start gap-2 border ${tone} rounded-sm px-3 py-2 text-xs`}
      data-testid="clinical-review-quota-badge">
      <Coins className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.75} />
      <div className="flex-1">
        <div>
          <span className="font-semibold">
            {used} / {cap} free AI reviews used
          </span>{" "}
          this month ({quota.period}).
          {paidExtras > 0 && (
            <span className="text-slate-600"> · {paidExtras} extra{paidExtras === 1 ? "" : "s"} purchased.</span>
          )}
        </div>
        {exhausted ? (
          <div className="mt-0.5">
            You've used all {cap} free reviews. The next review will cost <b>₹{quota.extra_price_inr}</b> via Razorpay.
            Free quota resets on the 1st.
          </div>
        ) : (
          <div className="mt-0.5 text-slate-600">
            {remaining} free review{remaining === 1 ? "" : "s"} left. After that, each extra review costs ₹{quota.extra_price_inr}.
          </div>
        )}
      </div>
    </div>
  );
}

function IntakeSection({ expanded, setExpanded, ext, setExt, rplMode, disabled }) {
  const patch = (k, v) => setExt((p) => ({ ...p, [k]: v }));
  return (
    <div className="border border-slate-200 rounded-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
        data-testid="intake-toggle-btn">
        <div>
          <div className="text-sm font-semibold text-slate-900">Extended clinical intake <span className="font-normal text-slate-500">(optional — improves report quality)</span></div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {expanded && (
        <fieldset disabled={disabled} className="p-4 border-t border-slate-200 grid sm:grid-cols-2 gap-4 disabled:opacity-60">
          <NumericField id="ext-age" label="Age (years)" value={ext.age} onChange={(v)=>patch("age", v)} testId="ext-age" />
          <NumericField id="ext-bmi" label="BMI (kg/m²)" value={ext.bmi} onChange={(v)=>patch("bmi", v)} step="0.1" testId="ext-bmi" />
          <NumericField id="ext-inf-yr" label="Infertility duration (years)" value={ext.infertility_years} onChange={(v)=>patch("infertility_years", v)} step="0.5" testId="ext-infyr" />
          <TextField id="ext-inf-type" label="Infertility type (primary / secondary)" value={ext.infertility_type} onChange={(v)=>patch("infertility_type", v)} testId="ext-inftype" />
          <NumericField id="ext-amh" label="AMH (ng/mL)" value={ext.amh} onChange={(v)=>patch("amh", v)} step="0.1" testId="ext-amh" />
          <NumericField id="ext-afc" label="AFC" value={ext.afc} onChange={(v)=>patch("afc", v)} testId="ext-afc" />

          <AreaField id="ext-oh" label="Obstetric history" placeholder="G / P, prior pregnancies, deliveries, terminations…" value={ext.obstetric_history} onChange={(v)=>patch("obstetric_history", v)} testId="ext-oh" />
          <AreaField id="ext-med" label="Current medications" placeholder="Levothyroxine, metformin, folate, etc." value={ext.medications} onChange={(v)=>patch("medications", v)} testId="ext-med" />
          <TextField id="ext-all" label="Allergies" value={ext.allergies} onChange={(v)=>patch("allergies", v)} testId="ext-all" />
          <TextField id="ext-life" label="Lifestyle notes" placeholder="Smoking / alcohol / exercise / occupation" value={ext.lifestyle} onChange={(v)=>patch("lifestyle", v)} testId="ext-life" />

          <TextField id="ext-tub" label="Tubal status" placeholder="e.g. bilateral hydrosalpinx, HSG normal" value={ext.tubal_status} onChange={(v)=>patch("tubal_status", v)} testId="ext-tub" />
          <TextField id="ext-uf" label="Uterine findings" placeholder="Fibroid, polyp, arcuate, adhesions…" value={ext.uterine_findings} onChange={(v)=>patch("uterine_findings", v)} testId="ext-uf" />
          <TextField id="ext-male" label="Male factor" placeholder="Semen analysis summary, DFI, etc." value={ext.male_factor} onChange={(v)=>patch("male_factor", v)} testId="ext-male" />

          <AreaField id="ext-ivf" label={rplMode ? "Prior ART cycles (if any)" : "Previous IVF/ICSI cycle history"}
            placeholder="Stim protocol, oocyte yield, fertilisation rate, blastocyst grade, transfer day, PGT, endometrial prep…"
            value={ext.ivf_cycle_history} onChange={(v)=>patch("ivf_cycle_history", v)} testId="ext-ivf" span2 />
          <TextField id="ext-endo" label="Endometrial preparation" placeholder="Natural / HRT / stimulated" value={ext.endometrial_prep} onChange={(v)=>patch("endometrial_prep", v)} testId="ext-endo" />
          <TextField id="ext-lut" label="Luteal support" placeholder="Type / dose / route" value={ext.luteal_support} onChange={(v)=>patch("luteal_support", v)} testId="ext-lut" />

          {rplMode && (
            <>
              <AreaField id="ext-rpl" label="RPL loss details"
                placeholder="Number of losses, gestational ages, ultrasound findings"
                value={ext.rpl_losses_detail} onChange={(v)=>patch("rpl_losses_detail", v)} testId="ext-rpl" span2 />
              <TextField id="ext-poc" label="Products of conception genetics" value={ext.poc_karyotype} onChange={(v)=>patch("poc_karyotype", v)} testId="ext-poc" />
              <TextField id="ext-pk" label="Parental karyotypes" value={ext.parental_karyotype} onChange={(v)=>patch("parental_karyotype", v)} testId="ext-pk" />
              <TextField id="ext-aps" label="APS testing results" placeholder="LA / aCL / anti-β2GP1 with dates" value={ext.aps_results} onChange={(v)=>patch("aps_results", v)} testId="ext-aps" />
            </>
          )}

          <AreaField id="ext-add" label="Additional clinical context"
            placeholder="Anything else relevant — imaging, prior surgeries, family history, patient preferences"
            value={ext.additional_notes} onChange={(v)=>patch("additional_notes", v)} testId="ext-add" span2 />
        </fieldset>
      )}
    </div>
  );
}

function NumericField({ id, label, value, onChange, step, testId }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" inputMode="decimal" step={step || "1"} value={value}
        onChange={(e)=>onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}
function TextField({ id, label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} placeholder={placeholder} onChange={(e)=>onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}
function AreaField({ id, label, value, onChange, placeholder, testId, span2 }) {
  return (
    <div className={span2 ? "sm:col-span-2" : ""}>
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} rows={3} value={value} placeholder={placeholder} onChange={(e)=>onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

/* ----------------- Rendered report ----------------- */
function ReviewReport({ review }) {
  return (
    <div className="space-y-6" data-testid="clinical-review-content">
      {review.missing_data_checklist?.length > 0 && (
        <Section icon={ClipboardList} title="Missing data to collect" tone="amber">
          <ul className="list-disc pl-6 space-y-1 text-sm text-slate-800">
            {review.missing_data_checklist.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </Section>
      )}

      {review.case_snapshot && (
        <Section icon={ClipboardList} title="1. Case snapshot">
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{review.case_snapshot}</p>
        </Section>
      )}

      {review.working_classification?.label && (
        <Section icon={LayoutGrid} title="2. Working classification">
          <div className="inline-block text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-sm border border-blue-900 bg-blue-900 text-white">
            {review.working_classification.label}
          </div>
          {review.working_classification.reasons?.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-slate-800">
              {review.working_classification.reasons.map((r, i) => (
                <li key={i} className="flex gap-2"><span className="text-blue-900">•</span>{r}</li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {review.priority_problems?.length > 0 && (
        <Section icon={ListChecks} title="3. Priority problems">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="text-left px-3 py-2 font-semibold w-12">#</th>
                  <th className="text-left px-3 py-2 font-semibold">Issue</th>
                  <th className="text-left px-3 py-2 font-semibold">Why it matters</th>
                  <th className="text-left px-3 py-2 font-semibold w-32">Evidence</th>
                  <th className="text-left px-3 py-2 font-semibold">Next action</th>
                </tr>
              </thead>
              <tbody>
                {review.priority_problems.map((row, i) => (
                  <tr key={i} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-2 font-mono font-bold text-blue-900">{row.priority}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{row.issue}</td>
                    <td className="px-3 py-2 text-slate-700">{row.why}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{row.evidence}</td>
                    <td className="px-3 py-2 text-slate-800">{row.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {review.stepwise_plan && (
        <Section icon={Beaker} title="4. Stepwise evaluation plan">
          <StepwiseSubblock title="Step 1 — Essential evaluation now" items={review.stepwise_plan.step1} tone="teal" />
          <StepwiseSubblock title="Step 2 — Consider only when clinically indicated" items={review.stepwise_plan.step2} tone="amber" />
          <StepwiseSubblock title="Step 3 — Not routinely recommended / insufficient evidence" items={review.stepwise_plan.step3} tone="slate" />
        </Section>
      )}

      {review.management_plan?.length > 0 && (
        <Section icon={Stethoscope} title="5. Personalised management plan">
          <div className="space-y-4">
            {review.management_plan.map((sec, si) => (
              <div key={si}>
                <div className="text-sm font-bold text-slate-900 mb-2">{sec.area}</div>
                <ul className="space-y-2">
                  {(sec.actions || []).map((a, ai) => (
                    <li key={ai} className="border-l-2 border-slate-200 pl-3 py-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-sm text-slate-900 font-semibold">{a.action}</span>
                        {a.category && (
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${CATEGORY_TONE[a.category] || "border-slate-300 bg-white text-slate-600"}`}>
                            {a.category}
                          </span>
                        )}
                      </div>
                      {a.who && <div className="text-xs text-slate-500 mt-1"><span className="uppercase tracking-widest text-[10px]">Who</span> · {a.who}</div>}
                      {a.rationale && <div className="text-xs text-slate-700 mt-0.5"><span className="uppercase tracking-widest text-[10px] text-slate-500">Rationale</span> · {a.rationale}</div>}
                      {a.risks && <div className="text-xs text-slate-700 mt-0.5"><span className="uppercase tracking-widest text-[10px] text-slate-500">Risks</span> · {a.risks}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {review.addons_table?.length > 0 && (
        <Section icon={LayoutGrid} title="6. IVF add-ons and controversies">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="text-left px-3 py-2 font-semibold">Intervention</th>
                  <th className="text-left px-3 py-2 font-semibold">Appropriate use</th>
                  <th className="text-left px-3 py-2 font-semibold">Why not routine</th>
                  <th className="text-left px-3 py-2 font-semibold">Evidence</th>
                  <th className="text-left px-3 py-2 font-semibold">Risks / cost</th>
                  <th className="text-left px-3 py-2 font-semibold">Guideline</th>
                </tr>
              </thead>
              <tbody>
                {review.addons_table.map((r, i) => (
                  <tr key={i} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-2 font-semibold text-slate-900">{r.intervention}</td>
                    <td className="px-3 py-2 text-slate-800">{r.appropriate_use}</td>
                    <td className="px-3 py-2 text-slate-700">{r.why_not_routine}</td>
                    <td className="px-3 py-2 text-slate-600">{r.evidence}</td>
                    <td className="px-3 py-2 text-slate-600">{r.risks}</td>
                    <td className="px-3 py-2 text-slate-600">{r.guideline}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {review.red_flags?.length > 0 && (
        <Section icon={ShieldAlert} title="7. Red flags and referrals">
          <ul className="space-y-2 text-sm">
            {review.red_flags.map((r, i) => (
              <li key={i} className="border-l-4 border-red-500 bg-red-50/40 pl-3 py-2 rounded-sm">
                <div className="font-semibold text-slate-900">{r.trigger}</div>
                <div className="text-xs text-slate-700 mt-0.5">
                  Refer to <span className="font-semibold">{r.referral}</span>
                  {" · "}
                  <span className={URGENCY_TONE[String(r.urgency).toLowerCase()] || ""}>{r.urgency}</span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(review.patient_counselling?.summary || review.patient_counselling?.questions?.length > 0) && (
        <Section icon={HeartHandshake} title="8. Patient counselling summary">
          {review.patient_counselling.summary && (
            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{review.patient_counselling.summary}</p>
          )}
          {review.patient_counselling.questions?.length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Questions the patient can ask</div>
              <ul className="space-y-1 text-sm text-slate-800">
                {review.patient_counselling.questions.map((q, i) => (
                  <li key={i} className="flex gap-2"><span className="text-blue-900 font-mono">Q{i+1}.</span>{q}</li>
                ))}
              </ul>
            </div>
          )}
        </Section>
      )}

      {review.disclaimer && (
        <div className="text-xs text-slate-500 italic border-t border-slate-200 pt-3">{review.disclaimer}</div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, tone, children }) {
  const toneClass = tone === "amber"
    ? "border-l-4 border-amber-500 bg-amber-50/40"
    : "border border-slate-200 bg-white";
  return (
    <section className={`${toneClass} rounded-sm p-4`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4 text-blue-900" strokeWidth={1.75} />}
        <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{title}</h4>
      </div>
      {children}
    </section>
  );
}

function StepwiseSubblock({ title, items, tone }) {
  if (!items || items.length === 0) return null;
  const tones = {
    teal: "border-teal-700 bg-teal-50/60",
    amber: "border-amber-500 bg-amber-50/60",
    slate: "border-slate-300 bg-slate-50/60",
  };
  return (
    <div className={`border-l-4 ${tones[tone]} pl-3 py-2 mb-3 rounded-sm`}>
      <div className="text-xs uppercase tracking-widest font-bold text-slate-700">{title}</div>
      <ul className="mt-2 space-y-2">
        {items.map((it, i) => (
          <li key={i}>
            <div className="text-sm font-semibold text-slate-900">{it.item}</div>
            {it.rationale && <div className="text-xs text-slate-700">{it.rationale}</div>}
            {it.changes_management && (
              <div className="text-xs text-slate-500 mt-0.5"><span className="uppercase tracking-widest text-[10px]">Impact</span> · {it.changes_management}</div>
            )}
            {(it.evidence || it.reference) && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                {it.evidence}{it.evidence && it.reference ? " · " : ""}{it.reference}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
